import { useState, useEffect, useCallback } from 'react';
import Layout from './Layout';
import { useAuth } from '../hooks/useAuth';
import { createAuthenticatedAxios } from '../utils/auth';
import '../styles.css';
import ErrorBoundary from './ErrorBoundary';
import { API_URL } from '../config';

function QuestionOfTheDay() {
  console.log('Rendering QuestionOfTheDay');
  const { isAuthenticated, isLoading, logout } = useAuth();
  const [question, setQuestion] = useState('');
  const [newAnswer, setNewAnswer] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [isSubscriber, setIsSubscriber] = useState(false);
  const [isQuestionLoading, setIsQuestionLoading] = useState(true);
  const [skipCount, setSkipCount] = useState({ skipsRemaining: 7, skipsUsed: 0 });
  const [isSkipping, setIsSkipping] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    console.log('QuestionOfTheDay useEffect - isAuthenticated:', isAuthenticated, 'isLoading:', isLoading);
    
    // Wait for authentication check to complete
    if (isLoading) {
      console.log('Still loading authentication status...');
      return;
    }
    
    if (!isAuthenticated) {
      console.log('Not authenticated, redirecting to login');
      window.location.href = '/login';
      return;
    }
    console.log('Authenticated, proceeding with data fetch');

    const fetchData = async () => {
      console.log('Fetching data...');
      setIsQuestionLoading(true);
      try {
        // Add a small delay to ensure authentication state is fully established
        await new Promise(resolve => setTimeout(resolve, 100));
        
        const axios = createAuthenticatedAxios(logout);
        console.log('Created authenticated axios instance');
        
        // Fetch user data, subscription status, and skip count in parallel
        const [userResponse, subscriptionResponse, skipCountResponse] = await Promise.all([
          axios.get('/api/user'),
          axios.get('/api/subscription/status'),
          axios.get('/api/question/skip-count')
        ]);
        
        console.log('User response:', userResponse.data);
        console.log('Subscription response:', subscriptionResponse.data);
        setIsSubscriber(subscriptionResponse.data.isPremium);
        setSkipCount(skipCountResponse.data);

        const questionsResponse = await axios.get('/api/question/daily');
        console.log('Daily question response:', questionsResponse.data);
        setQuestion(questionsResponse.data);
      } catch (err) {
        console.error('Fetch error details:', err);
        setError(err.response?.data?.error || 'Server error');
      } finally {
        setIsQuestionLoading(false);
      }
    };
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, isLoading]); // 'logout' intentionally omitted - it's stable from useAuth and shouldn't trigger re-fetch

  const handleSubmitAnswer = useCallback(async () => {
    if (!newAnswer.trim()) {
      setError('Please enter an answer before submitting');
      return;
    }
    
    if (!question || !question.id) {
      console.error('Question validation failed:', { question, hasId: question?.id });
      setError('No question loaded. Please refresh the page.');
      return;
    }
    
    setIsSubmitting(true);
    try {
      console.log('Starting answer submission...');
      console.log('Authentication status:', isAuthenticated);
      console.log('Question ID:', question.id);
      console.log('Answer text:', newAnswer);
      
      const axios = createAuthenticatedAxios(logout);
      console.log('Created authenticated axios for submission');
      
      const response = await axios.post('/api/answer', { questionId: question.id, text: newAnswer });
      console.log('Submit answer response:', response.data);
      setNewAnswer('');
      setSuccess('Answer submitted!');
      setTimeout(() => setSuccess(''), 3000);
      window.location.href = '/dashboard'; // Redirect to full dashboard after answer
    } catch (err) {
      console.error('Submit answer error details:', err);
      console.error('Error response:', err.response);
      console.error('Error message:', err.message);
      
      // Provide more specific error messages
      if (err.code === 'ECONNABORTED' || err.message.includes('timeout')) {
        setError('Request timed out. Please check your connection and try again.');
      } else if (err.response?.status === 401) {
        setError('Session expired. Please refresh the page and try again.');
      } else if (err.response?.status === 500) {
        setError('Server error. Please try again in a moment.');
      } else if (err.response?.data?.error) {
        setError(err.response.data.error);
      } else if (err.message) {
        setError(`Error: ${err.message}`);
      } else {
        setError('Failed to submit answer. Please try again.');
      }
    } finally {
      setIsSubmitting(false);
    }
  }, [newAnswer, question, isAuthenticated, logout]); // Memoize handler to prevent detachment

  const handleSkipQuestion = useCallback(async () => {
    if (skipCount.skipsRemaining <= 0) {
      setError('Daily skip limit reached. You can skip 7 questions per day.');
      return;
    }
    
    setIsSkipping(true);
    try {
      const axios = createAuthenticatedAxios(logout);
      const response = await axios.post('/api/question/skip', { questionId: question.id });
      
      // Update skip count
      setSkipCount({
        skipsRemaining: response.data.skipsRemaining,
        skipsUsed: response.data.skipsUsed
      });
      
      // Fetch new question (use random since we're skipping the daily one)
      const questionsResponse = await axios.get('/api/question/random');
      setQuestion(questionsResponse.data);
      setNewAnswer('');
      setSuccess('Question skipped!');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      if (err.response?.status === 429) {
        setError('Daily skip limit reached. You can skip 7 questions per day.');
        setSkipCount({
          skipsRemaining: 0,
          skipsUsed: err.response.data.skipsUsed
        });
      } else {
        setError(err.response?.data?.error || 'Server error');
      }
      console.error('Skip question error:', err);
    } finally {
      setIsSkipping(false);
    }
  }, [skipCount, question, logout]); // Memoize handler to prevent detachment

  return (
    <ErrorBoundary>
      <Layout>
        <div className="qotd-container container">
        <h1>Question of the Day</h1>
        {success && <p className="success">{success}</p>}
        {error && <p className="error">{error}</p>}
        {isLoading || isQuestionLoading ? (
          <p className="loading">Loading question...</p>
        ) : (
          <>
            <div style={{ marginBottom: '20px', textAlign: 'center' }}>
              <p style={{ fontSize: '14px', color: '#666', margin: '5px 0' }}>
                Skips remaining today: <strong>{skipCount.skipsRemaining}/7</strong>
              </p>
            </div>
            <p className="question-text">{question.text}</p>
            <textarea
              value={newAnswer}
              onChange={(e) => setNewAnswer(e.target.value)}
              placeholder="Your answer"
              rows="5" // Multi-line textarea with 5 rows
              className="answer-textarea" // Class for responsive styling
            />
            <div className="question-buttons" style={{ display: 'flex', gap: '10px', justifyContent: 'center', marginTop: '15px' }}>
              <button 
                onClick={handleSubmitAnswer}
                disabled={isSubmitting}
                className="btn-submit"
                style={{
                  opacity: isSubmitting ? 0.6 : 1,
                  cursor: isSubmitting ? 'not-allowed' : 'pointer'
                }}
              >
                {isSubmitting ? 'Submitting...' : 'Submit Answer'}
              </button>
              <button 
                onClick={handleSkipQuestion}
                disabled={skipCount.skipsRemaining <= 0 || isSkipping}
                className="btn-skip"
                style={{
                  opacity: (skipCount.skipsRemaining <= 0 || isSkipping) ? 0.6 : 1,
                  cursor: (skipCount.skipsRemaining <= 0 || isSkipping) ? 'not-allowed' : 'pointer'
                }}
              >
                {isSkipping ? 'Skipping...' : 'Skip Question'}
              </button>
            </div>
          </>
        )}
      </div>
    </Layout>
    </ErrorBoundary>
  );
}

export default QuestionOfTheDay;